import React, { useState, useEffect } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useWindowSize } from "usehooks-ts";
import type { CSS } from "@stitches/react";
import { styled, keyframes } from "../../../stitches.config";

const slideUpAndFade = keyframes({
  "0%": { opacity: 0, transform: "translateY(2px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const slideRightAndFade = keyframes({
  "0%": { opacity: 0, transform: "translateX(-2px)" },
  "100%": { opacity: 1, transform: "translateX(0)" },
});

const slideDownAndFade = keyframes({
  "0%": { opacity: 0, transform: "translateY(-2px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const slideLeftAndFade = keyframes({
  "0%": { opacity: 0, transform: "translateX(2px)" },
  "100%": { opacity: 1, transform: "translateX(0)" },
});

const TooltipContent = styled(TooltipPrimitive.Content, {
  borderRadius: 4,
  padding: "10px 15px",
  fontSize: 15,
  lineHeight: 1,
  color: "white",
  backgroundColor: "black",
  boxShadow:
    "hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px",
  "@media (prefers-reduced-motion: no-preference)": {
    animationDuration: "400ms",
    animationTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
    animationFillMode: "forwards",
    willChange: "transform, opacity",
    '&[data-state="delayed-open"]': {
      '&[data-side="top"]': { animationName: slideDownAndFade },
      '&[data-side="right"]': { animationName: slideLeftAndFade },
      '&[data-side="bottom"]': { animationName: slideUpAndFade },
      '&[data-side="left"]': { animationName: slideRightAndFade },
    },
  },
});

const StyledArrow = styled(TooltipPrimitive.Arrow, {
  fill: "black",
});

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  css?: CSS;
  arrowCss?: CSS;
  side?: "bottom" | "left" | "right" | "top";
  sideOffset?: number;
  showArrow?: boolean;
  align?: "center" | "end" | "start";
  forceClose?: boolean;
  hiddenMobile?: boolean;
}

export const BasicTooltip: React.FC<TooltipProps> = ({
  children,
  content,
  showArrow = true,
  arrowCss,
  forceClose,
  hiddenMobile,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { width } = useWindowSize();
  const isMobile = width <= 640;
  const touchDelayDuration = 500;
  let touchTimeout: any = null;

  useEffect(() => {
    if (forceClose) {
      setIsOpen(false);
    }
  }, [forceClose]);

  if (!content || (isMobile && hiddenMobile)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Provider {...props}>
      <TooltipPrimitive.Root open={isOpen}>
        <TooltipPrimitive.Trigger
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
          onTouchStart={() => {
            touchTimeout = setTimeout(() => {
              setIsOpen(true);
            }, touchDelayDuration);
          }}
          onTouchEnd={() => {
            clearTimeout(touchTimeout);
            setIsOpen(false);
          }}
          asChild
        >
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipContent side="top" align="center" {...props}>
          {content}
          {showArrow && (
            <StyledArrow offset={5} width={11} height={5} css={arrowCss} />
          )}
        </TooltipContent>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};
